/**
 * Teacher → Exams  (Phase-B — full ownership-scoped management)
 *
 * Adds to the read-only list:
 *   • Preview button        → modal renders the exam exactly as a student would see it
 *                              (with correct_answer stripped, from /preview endpoint)
 *   • Import questions      → CSV / XLSX upload with per-row error report
 *   • Live submissions      → dialog showing absent / in-progress / submitted counts
 *   • Manage (deep-link)    → falls back to admin UI for advanced flows
 *
 * All backend routes live under /api/school/portal/teacher/exams/*.
 */
import React, { useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  FileCheck, Loader2, ChevronRight, ListChecks, Eye, Upload, Users,
  ClipboardList, CheckCircle2, AlertCircle, CircleDashed, FileText, Sheet,
  Pencil, Plus, Trash2, Sparkles,
} from 'lucide-react';
import { portalApi } from './teacherApi';
import { getToken } from '@/lib/authToken';
import BatchQuestionDrafter from './BatchQuestionDrafter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () => {
  try { const p = sessionStorage.getItem('school_preview_token'); if (p) return p; }
  catch { /* ignore */ }
  return getToken();
};

const STATUS_COLOR = {
  draft:      'bg-slate-100 text-slate-700',
  scheduled:  'bg-sky-100 text-sky-700',
  active:     'bg-emerald-100 text-emerald-700',
  in_progress:'bg-emerald-100 text-emerald-700',
  marking:    'bg-amber-100 text-amber-700',
  in_marking: 'bg-amber-100 text-amber-700',
  closed:     'bg-slate-100 text-slate-500',
  published:  'bg-emerald-100 text-emerald-700',
  archived:   'bg-slate-100 text-slate-400',
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';

// ================== Question Editor Dialog ==================
const QUESTION_TYPES = [
  { id: 'mcq_single',   label: 'MCQ · Single answer' },
  { id: 'mcq_multi',    label: 'MCQ · Multiple answers' },
  { id: 'true_false',   label: 'True / False' },
  { id: 'short_answer', label: 'Short answer' },
  { id: 'long_essay',   label: 'Long essay' },
  { id: 'fill_blank',   label: 'Fill in the blank' },
  { id: 'matching',     label: 'Matching' },
];

const emptyQuestion = (subjectId) => ({
  subject_id: subjectId || '',
  question_type: 'mcq_single',
  question_text: '',
  options: ['', '', '', ''],
  correct_answer: '',
  correct_answers: [],
  marks: 1,
  difficulty: 'medium',
  explanation: '',
  topic: '',
  is_scripture: false,
});

const QuestionForm = ({ subjects, initial, onSave, onCancel, saving }) => {
  const [q, setQ] = useState(initial || emptyQuestion(subjects[0]?.subject_id || subjects[0]?.id));
  const [aiTopic, setAiTopic] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const usesOptions = ['mcq_single', 'mcq_multi'].includes(q.question_type);
  const isMulti = q.question_type === 'mcq_multi';

  const suggestWithClaude = async () => {
    if (!aiTopic.trim()) {
      toast.error('Give a topic first — e.g. "Photosynthesis basics".');
      return;
    }
    setAiBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/school/portal/teacher/questions/ai-suggest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${_token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: aiTopic,
          question_type: q.question_type,
          subject_id: q.subject_id,
          difficulty: q.difficulty || 'medium',
          is_scripture: !!q.is_scripture,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'AI request failed');
      const draft = await res.json();
      setQ(prev => ({
        ...prev,
        question_text: draft.question_text || prev.question_text,
        options: draft.options?.length ? draft.options : prev.options,
        correct_answer: draft.correct_answer ?? prev.correct_answer,
        correct_answers: draft.correct_answers?.length ? draft.correct_answers : prev.correct_answers,
        explanation: draft.explanation || prev.explanation,
        topic: draft.topic || prev.topic,
      }));
      toast.success('Claude drafted a question — review before saving');
    } catch (e) { toast.error(e.message); }
    finally { setAiBusy(false); }
  };

  const setField = (k, v) => setQ(prev => ({ ...prev, [k]: v }));
  const setOpt = (i, v) => setQ(prev => {
    const opts = [...(prev.options || [])];
    opts[i] = v;
    return { ...prev, options: opts };
  });
  const addOpt = () => setQ(prev => ({ ...prev, options: [...(prev.options || []), ''] }));
  const removeOpt = (i) => setQ(prev => {
    const opts = [...(prev.options || [])];
    opts.splice(i, 1);
    return { ...prev, options: opts };
  });

  return (
    <div className="border rounded p-3 space-y-2 bg-slate-50" data-testid="question-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Subject</Label>
          <Select value={q.subject_id} onValueChange={(v) => setField('subject_id', v)}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(subjects || []).map(s => (
                <SelectItem key={s.subject_id || s.id} value={s.subject_id || s.id}>
                  {s.subject_name || s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={q.question_type} onValueChange={(v) => setField('question_type', v)}>
            <SelectTrigger className="h-8" data-testid="qf-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {QUESTION_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Question text</Label>
        <textarea
          className="w-full text-sm border rounded p-2 min-h-[60px]"
          value={q.question_text}
          onChange={(e) => setField('question_text', e.target.value)}
          data-testid="qf-text"
        />
      </div>
      {/* Iter-412: Claude drafter — sits above the fields so teachers see it first */}
      <div className="border rounded p-2 bg-violet-50/60 space-y-1.5"
           data-testid="qf-ai-suggest">
        <Label className="text-xs flex items-center gap-1 text-violet-700">
          <Sparkles className="h-3.5 w-3.5" /> Suggest with Claude
        </Label>
        <div className="flex gap-1.5">
          <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
                 placeholder="Topic — e.g. 'Photosynthesis basics'"
                 className="h-8" data-testid="qf-ai-topic" />
          <Button size="sm" onClick={suggestWithClaude} disabled={aiBusy || !aiTopic.trim()}
                  data-testid="qf-ai-run">
            {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                     : <Sparkles className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">
          Draft fills the form below — review and edit before saving.
        </p>
      </div>
      {usesOptions && (
        <div>
          <Label className="text-xs">Options</Label>
          <div className="space-y-1">
            {(q.options || []).map((o, i) => {
              const isCorrect = isMulti
                ? (q.correct_answers || []).includes(o)
                : q.correct_answer === o;
              return (
                <div key={i} className="flex items-center gap-1">
                  <input
                    type={isMulti ? 'checkbox' : 'radio'}
                    checked={isCorrect}
                    onChange={() => {
                      if (isMulti) {
                        const cur = q.correct_answers || [];
                        setField('correct_answers',
                          cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o]);
                      } else {
                        setField('correct_answer', o);
                      }
                    }}
                    className="mt-1"
                    data-testid={`qf-opt-correct-${i}`}
                  />
                  <Input
                    value={o}
                    onChange={(e) => setOpt(i, e.target.value)}
                    className="h-8"
                    placeholder={`Option ${i + 1}`}
                    data-testid={`qf-opt-${i}`}
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8"
                          onClick={() => removeOpt(i)} disabled={(q.options || []).length <= 2}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
            <Button size="sm" variant="outline" onClick={addOpt}>
              <Plus className="h-3 w-3 mr-1" /> Add option
            </Button>
          </div>
        </div>
      )}
      {q.question_type === 'true_false' && (
        <div>
          <Label className="text-xs">Correct answer</Label>
          <Select value={q.correct_answer || ''} onValueChange={(v) => setField('correct_answer', v)}>
            <SelectTrigger className="h-8" data-testid="qf-tf-answer"><SelectValue placeholder="Pick" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="True">True</SelectItem>
              <SelectItem value="False">False</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {['short_answer', 'fill_blank'].includes(q.question_type) && (
        <div>
          <Label className="text-xs">Model answer (for reference)</Label>
          <Input value={q.correct_answer || ''} onChange={(e) => setField('correct_answer', e.target.value)}
                 data-testid="qf-model-answer" />
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Marks</Label>
          <Input type="number" min="0" step="0.5" value={q.marks}
                 onChange={(e) => setField('marks', Number(e.target.value))}
                 className="h-8" data-testid="qf-marks" />
        </div>
        <div>
          <Label className="text-xs">Difficulty</Label>
          <Select value={q.difficulty} onValueChange={(v) => setField('difficulty', v)}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">Easy</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="hard">Hard</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Topic (optional)</Label>
          <Input value={q.topic || ''} onChange={(e) => setField('topic', e.target.value)}
                 className="h-8" data-testid="qf-topic" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Explanation / marking guide</Label>
        <textarea
          className="w-full text-sm border rounded p-2 min-h-[40px]"
          value={q.explanation || ''}
          onChange={(e) => setField('explanation', e.target.value)}
          data-testid="qf-explanation"
        />
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={!!q.is_scripture}
               onChange={(e) => setField('is_scripture', e.target.checked)}
               data-testid="qf-is-scripture" />
        Scripture-based question
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={() => onSave(q)}
                disabled={saving || !q.question_text.trim()}
                data-testid="qf-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
          Save question
        </Button>
      </div>
    </div>
  );
};

const QuestionsDialog = ({ examId, subjects, open, onOpenChange }) => {
  const [editing, setEditing] = useState(null);  // question id or 'new'
  const [saving, setSaving] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const { data: questions = [], refetch, isLoading } = useQuery({
    queryKey: ['exam-questions', examId],
    queryFn: () => portalApi(`/teacher/exams/${examId}/questions`),
    enabled: !!examId && open,
  });

  const call = async (path, opts) => {
    const res = await fetch(`${API_URL}/api/school/portal/teacher${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${_token()}`, 'Content-Type': 'application/json',
                 ...(opts.headers || {}) },
    });
    if (!res.ok) throw new Error((await res.json()).detail || `Failed (${res.status})`);
    return res.json();
  };

  const save = async (q) => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await call(`/exams/${examId}/questions`, { method: 'POST', body: JSON.stringify(q) });
        toast.success('Question added');
      } else {
        await call(`/exams/${examId}/questions/${editing}`,
          { method: 'PATCH', body: JSON.stringify(q) });
        toast.success('Question updated');
      }
      setEditing(null);
      refetch();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await call(`/exams/${examId}/questions/${qid}`, { method: 'DELETE' });
      toast.success('Question deleted');
      refetch();
    } catch (e) { toast.error(e.message); }
  };

  const asFormValue = (row) => {
    const q = row.question || {};
    return {
      subject_id: row.subject_id,
      question_type: q.question_type || row.question_type,
      question_text: q.question_text || row.question_text || '',
      options: q.options || row.options || ['', ''],
      correct_answer: q.correct_answer || null,
      correct_answers: q.correct_answers || [],
      marks: row.marks ?? q.marks ?? 1,
      difficulty: q.difficulty || 'medium',
      explanation: q.explanation || '',
      topic: q.topic || '',
      is_scripture: !!q.is_scripture,
    };
  };

  const editingRow = questions.find(r => r.question_id === editing);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="exam-questions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <ListChecks className="h-4 w-4" /> Questions
              <Badge variant="outline">{questions.length}</Badge>
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                onClick={() => setBatchOpen(true)}
                disabled={!!editing}
                data-testid="batch-draft-btn"
              >
                <Sparkles className="h-4 w-4 mr-1" /> Batch draft
              </Button>
              <Button size="sm" onClick={() => setEditing('new')} disabled={!!editing}
                      data-testid="add-question-btn">
                <Plus className="h-4 w-4 mr-1" /> Add question
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto space-y-2 pr-2">
          {editing === 'new' && (
            <QuestionForm subjects={subjects} initial={null}
                          onSave={save} onCancel={() => setEditing(null)} saving={saving} />
          )}
          {isLoading ? (
            <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : questions.length === 0 && editing !== 'new' ? (
            <div className="text-center text-slate-500 p-6 border border-dashed rounded text-sm">
              No questions yet — click <b>Add question</b> or use <b>Import</b>.
            </div>
          ) : (
            questions.map((row) => {
              const q = row.question || row;
              const qid = row.question_id;
              if (editing === qid) {
                return (
                  <QuestionForm key={qid} subjects={subjects} initial={asFormValue(row)}
                                onSave={save} onCancel={() => setEditing(null)} saving={saving} />
                );
              }
              return (
                <div key={qid} className="border rounded p-2 flex items-start gap-2"
                     data-testid={`question-row-${qid}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <Badge variant="outline">{q.question_type}</Badge>
                      <span className="text-slate-500">{row.marks ?? q.marks ?? 1} mk</span>
                      {q.difficulty && <span className="text-slate-400">· {q.difficulty}</span>}
                      {q.is_scripture && <Badge className="bg-purple-100 text-purple-700">scripture</Badge>}
                    </div>
                    <p className="text-sm mt-1">{q.question_text}</p>
                    {q.options?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Options: {q.options.join(' · ')}
                      </p>
                    )}
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => setEditing(qid)}
                          disabled={!!editing} data-testid={`edit-question-${qid}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(qid)}
                          disabled={!!editing} data-testid={`delete-question-${qid}`}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                  </Button>
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
      <BatchQuestionDrafter
        examId={examId}
        subjects={subjects}
        open={batchOpen}
        onOpenChange={setBatchOpen}
        onSaved={refetch}
      />
    </Dialog>
  );
};

// ================== Preview Modal ==================
const PreviewDialog = ({ examId, open, onOpenChange }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['exam-preview', examId],
    queryFn: () => portalApi(`/teacher/exams/${examId}/preview`),
    enabled: !!examId && open,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="exam-preview-dialog">
        <DialogHeader><DialogTitle>Preview — {data?.name || 'Exam'}</DialogTitle></DialogHeader>
        {isLoading ? (
          <div className="p-6 text-center"><Loader2 className="h-6 w-6 animate-spin inline-block" /></div>
        ) : error ? (
          <div className="text-sm text-rose-600">Failed to load preview</div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto space-y-4 pr-2">
            {data?.instructions && (
              <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded whitespace-pre-wrap">
                {data.instructions}
              </div>
            )}
            {(data?.subjects || []).map((s, i) => (
              <div key={s.subject_id || i} className="border rounded p-3 space-y-3"
                   data-testid={`preview-subject-${s.subject_id}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <Badge className="bg-indigo-100 text-indigo-700">{s.subject_name}</Badge>
                  {s.max_marks != null && <span>Max: {s.max_marks}</span>}
                  {s.duration_minutes && <span>· {s.duration_minutes} min</span>}
                </div>
                {(s.questions || []).length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No questions yet.</p>
                ) : (
                  <ol className="space-y-3 list-decimal list-inside">
                    {s.questions.map((q, j) => (
                      <li key={q.id || j} className="text-sm">
                        <span className="font-medium">{q.question_text}</span>
                        {' '}<Badge variant="outline" className="text-[10px] ml-1">{q.question_type}</Badge>
                        {q.marks != null && <span className="text-xs text-slate-500 ml-1">({q.marks} mk)</span>}
                        {q.options?.length > 0 && (
                          <ul className="ml-6 mt-1 space-y-0.5 text-xs text-slate-600">
                            {q.options.map((o, k) => <li key={k}>○ {o}</li>)}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== Import Dialog ==================
const ImportDialog = ({ examId, subjects, open, onOpenChange, onDone }) => {
  const [subjectId, setSubjectId] = useState('');
  const [file, setFile] = useState(null);
  const [gsheetUrl, setGsheetUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      setSubjectId(subjects?.[0]?.subject_id || subjects?.[0]?.id || '');
      setFile(null); setGsheetUrl(''); setResult(null); setBusy(false);
    }
  }, [open, subjects]);

  const runFile = async () => {
    if (!file || !subjectId) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('subject_id', subjectId);
      const res = await fetch(`${API_URL}/api/school/portal/teacher/exams/${examId}/questions/import`,
        { method: 'POST', headers: { Authorization: `Bearer ${_token()}` }, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || 'Import failed');
      const r = await res.json();
      setResult(r);
      toast.success(`${r.imported} imported · ${r.skipped} skipped`);
      onDone?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const runGsheet = async () => {
    if (!gsheetUrl || !subjectId) return;
    setBusy(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/school/portal/teacher/exams/${examId}/questions/import-gsheet`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${_token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: gsheetUrl, subject_id: subjectId }),
        });
      if (!res.ok) throw new Error((await res.json()).detail || 'Import failed');
      const r = await res.json();
      setResult(r);
      toast.success(`${r.imported} imported · ${r.skipped} skipped`);
      onDone?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="exam-import-dialog">
        <DialogHeader><DialogTitle>Import questions</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Target subject</Label>
            <Select value={subjectId} onValueChange={setSubjectId}>
              <SelectTrigger data-testid="import-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(subjects || []).map(s => (
                  <SelectItem key={s.subject_id || s.id} value={s.subject_id || s.id}>
                    {s.subject_name || s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Tabs defaultValue="file">
            <TabsList className="w-full">
              <TabsTrigger value="file" data-testid="import-tab-file" className="flex-1">
                <FileText className="h-3.5 w-3.5 mr-1" /> CSV / XLSX
              </TabsTrigger>
              <TabsTrigger value="gsheet" data-testid="import-tab-gsheet" className="flex-1">
                <Sheet className="h-3.5 w-3.5 mr-1" /> Google Sheets URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="space-y-2 pt-3">
              <p className="text-[11px] text-slate-500">
                Columns: <code className="text-[10px]">question_type, question_text, options, correct_answer, correct_answers, marks, difficulty, explanation, tags, topic, is_scripture</code> (pipe-separated multi-value cells).
              </p>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                       onChange={(e) => setFile(e.target.files?.[0] || null)}
                       data-testid="import-file-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
                        data-testid="import-pick-file">
                  <Upload className="h-4 w-4 mr-1" /> Pick file
                </Button>
                <span className="text-xs text-slate-500 truncate">{file?.name || 'No file'}</span>
              </div>
              <Button onClick={runFile} disabled={!file || !subjectId || busy}
                      className="w-full" data-testid="import-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Import file
              </Button>
            </TabsContent>

            <TabsContent value="gsheet" className="space-y-2 pt-3">
              <p className="text-[11px] text-slate-500">
                Paste any Google Sheets URL. Set sharing to <b>Anyone with the link · Viewer</b> — no OAuth needed.
              </p>
              <Input value={gsheetUrl} onChange={(e) => setGsheetUrl(e.target.value)}
                     placeholder="https://docs.google.com/spreadsheets/d/…"
                     data-testid="import-gsheet-url" />
              <Button onClick={runGsheet} disabled={!gsheetUrl || !subjectId || busy}
                      className="w-full" data-testid="import-gsheet-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sheet className="h-4 w-4 mr-1" />}
                Import from Google Sheets
              </Button>
            </TabsContent>
          </Tabs>

          {result && (
            <div className="text-xs bg-slate-50 border rounded p-2 space-y-1">
              <div>
                <CheckCircle2 className="h-3 w-3 inline text-emerald-600 mr-1" />
                {result.imported} imported
                {result.skipped > 0 && <> · <AlertCircle className="h-3 w-3 inline text-amber-600 mx-1" />{result.skipped} skipped</>}
              </div>
              {result.errors?.length > 0 && (
                <ul className="mt-1 text-rose-600 max-h-32 overflow-auto">
                  {result.errors.map((e, i) => <li key={i}>· {e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== Live Submissions Dialog ==================
const LiveDialog = ({ examId, open, onOpenChange }) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['exam-live', examId],
    queryFn: () => portalApi(`/teacher/exams/${examId}/live-submissions`),
    enabled: !!examId && open,
    refetchInterval: open ? 10000 : false, // live-poll every 10s
  });
  const rows = data?.rows || [];
  const c = data?.counts || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="exam-live-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" /> Live submissions
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline"><Users className="h-3 w-3 mr-1" />{c.total || 0}</Badge>
          <Badge className="bg-emerald-100 text-emerald-700">{c.submitted || 0} submitted</Badge>
          <Badge className="bg-amber-100 text-amber-700">{c.in_progress || 0} in progress</Badge>
          <Badge className="bg-slate-100 text-slate-700">{c.absent || 0} absent</Badge>
          <Button size="sm" variant="ghost" onClick={() => refetch()} className="ml-auto h-6">
            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        <div className="max-h-80 overflow-auto border rounded">
          {rows.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">
              <CircleDashed className="h-6 w-6 mx-auto mb-1 opacity-50" />
              No students enrolled in this exam yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs">
                <tr>
                  <th className="text-left p-2">Student</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Last save</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.student_id} className="border-t"
                      data-testid={`live-row-${r.student_id}`}>
                    <td className="p-2">
                      {r.student_name}
                      {r.admission_number && <span className="text-xs text-slate-400 ml-1">({r.admission_number})</span>}
                    </td>
                    <td className="p-2">
                      <Badge className={STATUS_COLOR[r.state === 'submitted' ? 'active'
                                        : r.state === 'in_progress' ? 'marking' : 'draft']}>
                        {r.state}
                      </Badge>
                    </td>
                    <td className="p-2 text-xs text-slate-500">{fmt(r.last_save)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== Main ==================
export const TeacherExams = () => {
  const [status, setStatus] = useState('all');
  const [previewId, setPreviewId] = useState(null);
  const [importId, setImportId] = useState(null);
  const [liveId, setLiveId] = useState(null);
  const [questionsId, setQuestionsId] = useState(null);

  const { data: exams = [], isLoading, refetch } = useQuery({
    queryKey: ['teacher-exams', status],
    queryFn: () => portalApi(`/teacher/exams${status !== 'all' ? `?status=${status}` : ''}`),
  });

  const importExam = exams.find(e => e.id === importId);
  const importSubjects = (importExam?.subjects) || [];

  return (
    <div className="space-y-3" data-testid="teacher-exams-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-indigo-500" /> Exams <Badge variant="outline">{exams.length}</Badge>
        </h2>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="exams-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="marking">In marking</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading exams…</div>
      ) : exams.length === 0 ? (
        <div className="text-center text-slate-500 p-8 border border-dashed rounded">
          You haven&apos;t created any exams yet. Ask your admin to grant ownership or use
          <strong> Admin → School → Exams</strong> to create one.
        </div>
      ) : (
        <div className="space-y-2">
          {exams.map((e) => (
            <Card key={e.id} data-testid={`teacher-exam-${e.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-sm">{e.name || e.title}</h3>
                      <Badge className={STATUS_COLOR[e.status] || STATUS_COLOR.draft}>{e.status || 'draft'}</Badge>
                      {e.exam_type && <Badge variant="outline" className="text-[10px]">{e.exam_type}</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                      <span><ListChecks className="h-3 w-3 inline mr-0.5" />{e.question_count} questions</span>
                      <span>{e.submission_count} submitted · {e.marked_count} marked</span>
                      {e.scheduled_at && <span>Scheduled {fmt(e.scheduled_at)}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setQuestionsId(e.id)}
                            data-testid={`exam-questions-${e.id}`}>
                      <ListChecks className="h-3.5 w-3.5 mr-1" /> Questions
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPreviewId(e.id)}
                            data-testid={`exam-preview-${e.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setImportId(e.id)}
                            data-testid={`exam-import-${e.id}`}>
                      <Upload className="h-3.5 w-3.5 mr-1" /> Import
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setLiveId(e.id)}
                            data-testid={`exam-live-${e.id}`}>
                      <ClipboardList className="h-3.5 w-3.5 mr-1" /> Live
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <a href={`/admin/schools/${e.tenant_id}/exams/${e.id}`} target="_blank" rel="noopener noreferrer">
                        Manage <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PreviewDialog examId={previewId} open={!!previewId}
                     onOpenChange={(v) => !v && setPreviewId(null)} />
      <QuestionsDialog examId={questionsId} subjects={
        (exams.find(e => e.id === questionsId)?.subjects) || []
      } open={!!questionsId}
                       onOpenChange={(v) => !v && setQuestionsId(null)} />
      <ImportDialog examId={importId} subjects={importSubjects} open={!!importId}
                    onOpenChange={(v) => !v && setImportId(null)}
                    onDone={refetch} />
      <LiveDialog examId={liveId} open={!!liveId}
                  onOpenChange={(v) => !v && setLiveId(null)} />
    </div>
  );
};

export default TeacherExams;
